import React, { useContext, useEffect, useState, useMemo } from "react";
import { Container, Button, Row } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import SectionCard from "Components/Common/SectionCard";
import DetailField from "Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import { formatDate } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import { loadCodelistsDetailId } from "api/modules/common.option";
import TruncatedText from "Components/Common/TruncatedText";

const ViewAmbulatoryVisit: React.FC = () => {
  document.title = "View Ambulatory Visit | ZENTIXS VISTA";

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const breadcrumbContext = useContext(BreadcrumbContext);

  const [visit, setVisit] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const studyid = Number(sessionStorage.getItem("activestudyid") || 1);
  const { SelectdataAmbulatoryVisit } = useAmbulatoryVisitService({
    studyid,
    visitid: Number(id)
  });

  // Fetch options
  useEffect(() => {
    loadCodelistsDetailId(["AMBULATORY_VISIT_ACTIVITY_CATEGORY_OPTIONS"]).then(() => {
      setOptionsLoaded(true);
    });
  }, []);

  // Breadcrumbs sync
  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Ambulatory Visits", link: `/study/detail/ambulatory` },
      { text: "View Ambulatory Visit" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [studyid]);

  // Set visit data once fetched
  useEffect(() => {
    if (SelectdataAmbulatoryVisit.data) {
      const raw = (SelectdataAmbulatoryVisit.data as any)?.data;
      const resData = raw?.data || raw;

      setVisit(resData?.visit?.[0] || resData?.visit || resData?.list1?.[0] || null);

      const acts =
        resData?.samplingpoints ||
        resData?.["samplingpoints "]||
        [];

      setActivities(acts);
    }
  }, [SelectdataAmbulatoryVisit.data]);

  const samplingPointsDisplay = useMemo(() => {
    if (Array.isArray(activities) && activities.length > 0) {
      const list = activities
        .map((a: any) => (typeof a === "object" ? a.samplingpoint_number || a.samplingpoint_name || a.samplingpoint || a.number : String(a)))
        .filter(Boolean);
      if (list.length > 0) return list.join(", ");
    }
    if (visit) {
      const sp = visit.samplingpoints || visit.sampling_points;
      if (Array.isArray(sp) && sp.length > 0) return sp.filter(Boolean).join(", ");
      if (typeof sp === "string" && sp.trim()) return sp;
    }
    return "--";
  }, [activities, visit]);

  const deviationDisplay = visit?.visitwindowdeviation || "--";

  if (SelectdataAmbulatoryVisit.isLoading || SelectdataAmbulatoryVisit.isFetching || SelectdataAmbulatoryVisit.isPending || !optionsLoaded) {
    return (
      <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
        <FullPageLoader />
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Ambulatory Visit Not Found</h4>
            <p className="text-muted">
              The Ambulatory Visit record you are looking for does not exist or has been removed.
            </p>
            <Button color="primary" onClick={() => navigate(`/study/detail/ambulatory`)} className="mt-3">
              Back to List
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>

        {/* Header Block */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">Ambulatory Visit Details</h3>
            <p className="text-muted mb-0 d-flex align-items-center flex-wrap" style={{ gap: "4px" }}>
              <span>Visit Number: {visit.visitnumber ?? id}</span>
              <span className="mx-1">|</span>
              <span>Visit Name:</span>
              <TruncatedText text={visit.visitname || "--"} maxWidth="200px" />
              <span className="mx-1">|</span>
              <span>Visit Type: {visit.visittypename}</span>
            </p>
          </div>
        </div>

        <div className="d-flex flex-column ">

          {/* Section 1: Study Visit Details */}
          <SectionCard
            icon="ri-file-list-3-line"
            title="Visit Information"
            subtitle="View basic details, type, category, sampling points, and deviation for this ambulatory visit."
          >
            <Row>
              <DetailField label="Visit Name" value={visit.visitname} columns={4} truncate={true} />
              <DetailField
                label="Visit Type"
                value={visit.visittypename}
                columns={4}
              />
              <DetailField
                label="Visit Category"
                value={visit.visitcategoryname}
                columns={4}
              />
              <DetailField
                label="Sampling Point"
                value={samplingPointsDisplay}
                columns={4}
              />
              <DetailField
                label="Allowed Deviation Time"
                value={deviationDisplay}
                columns={4}
              />
              <DetailField
                label="Visit Description"
                value={visit.visitdescription || "--"}
                columns={12}
                expandable={true}
                maxChars={200}
              />
            </Row>
          </SectionCard>

        </div>
      </Container>
    </div>
  );
};

export default ViewAmbulatoryVisit;
